import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { Globals } from '../../services/globals.service';
import { UserService } from '../../services/user.service';
import { ApplicationService } from '../../services/application.service';
import { InitializationService } from '../../services/initialization.service';
import { ProfileService } from '../../services/profile.service';
import { BaseComponent } from '../../classes/base-component';
import { TrackAnalyticsService } from '../../services/track-analytics.service';
import { LocalOptInService } from '../../services/local-opt-in.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-household',
    templateUrl: './household.component.html',
    styleUrls: ['./household.component.scss'],
    viewProviders: [],
    providers: [Globals],
})
export class HouseholdComponent extends BaseComponent implements OnInit {
    username: string = null;
    fullname: string = null;

    household: any = {
        Attributes: {
            PropertyType: null,
            PropertySize: null,
            PropertyAge: null,
            Occupants: null,
            SpaceHeating: null,
            WaterHeating: null,
            OvenHeating: null,
            GrillHeating: null,
            StoveHeating: null,
            Photovoltaic: null,
        },
        Appliances: [],
    };

    propertyType: any;
    propertySize: any;
    propertyAge: any;
    occupants: any;
    spaceHeating: any;
    waterHeating: any;
    photovoltaic: any;
    appliances: any;

    state = 'none';

    constructor(
        private title: Title,
        private toastrService: ToastrService,
        private globals: Globals,
        private userService: UserService,
        public application: ApplicationService,
        private initialization: InitializationService,
        private profile: ProfileService,
        private analytics: TrackAnalyticsService,
        private optInService: LocalOptInService,
        private translate: TranslateService
    ) {
        super();
    }

    ngOnInit() {
        this.initializeDataStructures();

        this.translate.get('screens.household.pageTitle').subscribe((translatedTitle: string) => {
            this.title.setTitle(translatedTitle);
        });
        this.optInService.checkStatus();
        this.username = this.userService.getActiveUserName();

        this.initialize();

        // Subscribe to language changes to update all translations
        this.addSub(
            this.translate.onLangChange.subscribe(() => {
                this.updateAllTranslations();
            })
        );
    }

    initialize(): void {
        this.addSub(
            this.initialization.get().subscribe((res) => {
                if (res) {
                    if ('profile' in res) {
                        this.processContractData(res);
                    }
                }
            })
        );

        this.addSub(
            this.profile.getAttributes().subscribe((res) => {
                if (res) {
                    this.processProfileData(res);
                }
            })
        );
    }

    /**
     * Update all translations when language changes
     */
    private updateAllTranslations(): void {
        // Simply recreate all the data structures with new translations
        this.initializeDataStructures();
    }

    /**
     * Initialize all data structures with current translations
     */
    private initializeDataStructures(): void {
        this.propertyType = {
            title: this.translate.instant('screens.household.propertyQuestion'),
            selectedValue: this.propertyType?.selectedValue || null,
            items: [
                {
                    title: this.translate.instant(
                        'screens.household.propertyTypes.freistehendesHaus'
                    ),
                    iconName: 'PT.01',
                },
                {
                    title: this.translate.instant(
                        'screens.household.propertyTypes.doppelhaushälfte'
                    ),
                    iconName: 'PT.02',
                },
                {
                    title: this.translate.instant('screens.household.propertyTypes.bungalow'),
                    iconName: 'PT.03',
                },
                {
                    title: this.translate.instant('screens.household.propertyTypes.reihenhaus'),
                    iconName: 'PT.04',
                },
                {
                    title: this.translate.instant('screens.household.propertyTypes.reiheneckhaus'),
                    iconName: 'PT.05',
                },
                {
                    title: this.translate.instant('screens.household.propertyTypes.wohnung'),
                    iconName: 'PT.06',
                },
            ],
        };

        this.propertySize = {
            title: this.translate.instant('screens.household.sizeQuestion'),
            selectedValue: this.propertySize?.selectedValue || null,
            items: [
                {
                    title: this.translate.instant('screens.household.propertySizes.oneZimmer'),
                    iconName: 'PS.01',
                },
                {
                    title: this.translate.instant('screens.household.propertySizes.twoZimmer'),
                    iconName: 'PS.02',
                },
                {
                    title: this.translate.instant('screens.household.propertySizes.threeZimmer'),
                    iconName: 'PS.03',
                },
                {
                    title: this.translate.instant('screens.household.propertySizes.fourPlusZimmer'),
                    iconName: 'PS.04',
                },
            ],
        };

        this.propertyAge = {
            title: this.translate.instant('screens.household.ageQuestion'),
            selectedValue: this.propertyAge?.selectedValue || null,
            items: [
                {
                    title: this.translate.instant('screens.household.propertyAges.before1919'),
                    iconName: 'PA.01',
                },
                {
                    title: this.translate.instant('screens.household.propertyAges.1920to1975'),
                    iconName: 'PA.02',
                },
                {
                    title: this.translate.instant('screens.household.propertyAges.1979to1999'),
                    iconName: 'PA.03',
                },
                {
                    title: this.translate.instant('screens.household.propertyAges.after2000'),
                    iconName: 'PA.04',
                },
            ],
        };

        this.occupants = {
            title: this.translate.instant('screens.household.occupantsQuestion'),
            selectedValue: this.occupants?.selectedValue || null,
            items: [
                {
                    title: this.translate.instant('screens.household.occupants.one'),
                    iconName: 'OCC.01',
                },
                {
                    title: this.translate.instant('screens.household.occupants.two'),
                    iconName: 'OCC.02',
                },
                {
                    title: this.translate.instant('screens.household.occupants.three'),
                    iconName: 'OCC.03',
                },
                {
                    title: this.translate.instant('screens.household.occupants.four'),
                    iconName: 'OCC.04',
                },
                {
                    title: this.translate.instant('screens.household.occupants.fivePlus'),
                    iconName: 'OCC.05',
                },
            ],
        };

        this.spaceHeating = {
            title: this.translate.instant('screens.household.spaceHeatingQuestion'),
            selectedValue: this.spaceHeating?.selectedValue || null,
            items: [
                {
                    title: this.translate.instant('screens.household.spaceHeating.gasheizung'),
                    iconName: 'SH.01',
                },
                {
                    title: this.translate.instant('screens.household.spaceHeating.waermespeicher'),
                    iconName: 'SH.02',
                },
                {
                    title: this.translate.instant('screens.household.spaceHeating.elektroheizung'),
                    iconName: 'SH.03',
                },
                {
                    title: this.translate.instant('screens.household.spaceHeating.warmepumpe'),
                    iconName: 'SH.04',
                },
                {
                    title: this.translate.instant('screens.household.spaceHeating.oelheizung'),
                    iconName: 'SH.05',
                },
                {
                    title: this.translate.instant('screens.household.spaceHeating.solarzelle'),
                    iconName: 'SH.06',
                },
                {
                    title: this.translate.instant(
                        'screens.household.spaceHeating.hybridWarmepumpe'
                    ),
                    iconName: 'SH.08',
                },
                {
                    title: this.translate.instant('screens.household.spaceHeating.fernwaerme'),
                    iconName: 'SH.09',
                },
                {
                    title: this.translate.instant('screens.household.spaceHeating.holzpellets'),
                    iconName: 'SH.10',
                },
                {
                    title: this.translate.instant('screens.household.spaceHeating.andere'),
                    iconName: 'SH.07',
                },
            ],
        };

        this.waterHeating = {
            title: this.translate.instant('screens.household.waterHeatingQuestion'),
            selectedValue: this.waterHeating?.selectedValue || null,
            items: [
                {
                    title: this.translate.instant('screens.household.waterHeating.gasheizkessel'),
                    iconName: 'WH.01',
                },
                {
                    title: this.translate.instant('screens.household.waterHeating.elektroboiler'),
                    iconName: 'WH.02',
                },
                {
                    title: this.translate.instant('screens.household.waterHeating.oelheizkessel'),
                    iconName: 'WH.03',
                },
                {
                    title: this.translate.instant('screens.household.waterHeating.solartherme'),
                    iconName: 'WH.04',
                },
                {
                    title: this.translate.instant('screens.household.waterHeating.andere'),
                    iconName: 'WH.05',
                },
            ],
        };

        this.photovoltaic = {
            title: this.translate.instant('screens.household.photovoltaicQuestion'),
            selectedValue: this.photovoltaic?.selectedValue || null,
            items: [
                {
                    title: this.translate.instant('screens.household.photovoltaic.yes'),
                    iconName: 'SOL.01',
                },
                {
                    title: this.translate.instant('screens.household.photovoltaic.no'),
                    iconName: 'SOL.02',
                },
            ],
        };

        // For appliances, preserve selected state and count
        const currentAppliances = this.appliances?.items || [];
        this.appliances = {
            title: this.translate.instant('screens.household.appliancesQuestion'),
            items: [
                {
                    title: this.translate.instant(
                        'screens.household.appliances.freezerCombination'
                    ),
                    iconName: 'A.03',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.fridge'),
                    iconName: 'A.01',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.freezer'),
                    iconName: 'A.02',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.electricOven'),
                    iconName: 'A.04',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.electricGrill'),
                    iconName: 'A.05',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.electricStove'),
                    iconName: 'A.06',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.microwave'),
                    iconName: 'A.07',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.kettle'),
                    iconName: 'A.08',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.toaster'),
                    iconName: 'A.09',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.dishwasher'),
                    iconName: 'A.10',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.washingMachine'),
                    iconName: 'A.11',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.dryer'),
                    iconName: 'A.12',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.iron'),
                    iconName: 'A.13',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.tv'),
                    iconName: 'A.14',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.dvdOrBluRayPlayer'),
                    iconName: 'A.15',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.digitalTvBox'),
                    iconName: 'A.16',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.gameConsole'),
                    iconName: 'A.17',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.computer'),
                    iconName: 'A.18',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.tablet'),
                    iconName: 'A.19',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant(
                        'screens.household.appliances.instantaneousWaterHeater'
                    ),
                    iconName: 'A.20',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.wallbox'),
                    iconName: 'A.21',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.pool'),
                    iconName: 'A.22',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.sauna'),
                    iconName: 'A.23',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.airConditioning'),
                    iconName: 'A.25',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.waterBoiler'),
                    iconName: 'A.27',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant(
                        'screens.household.appliances.underSinkWaterBoiler'
                    ),
                    iconName: 'A.28',
                    count: 0,
                    selected: false,
                },
                {
                    title: this.translate.instant('screens.household.appliances.batteryStorage'),
                    iconName: 'A.29',
                    count: 0,
                    selected: false,
                },
            ],
        };

        // Restore appliance selections and counts
        this.appliances.items.forEach((newItem) => {
            const existingItem = currentAppliances.find(
                (item) => item.iconName === newItem.iconName
            );
            if (existingItem) {
                newItem.count = existingItem.count;
                newItem.selected = existingItem.selected;
            }
        });
    }

    determineAppliancesSelected(): boolean {
        for (const element of this.appliances.items) {
            if (element.selected) {
                return true;
            }
        }
        return false;
    }

    determineImagePath(element, currentItem): string {
        const state = element.selectedValue === currentItem.iconName ? 'active' : 'inactive';
        return `/assets/img/icons/household/${state}/${currentItem.iconName}.png`;
    }

    determineApplianceImagePath(currentItem): string {
        const state = currentItem.selected ? 'active' : 'inactive';
        return `/assets/img/icons/household/${state}/${currentItem.iconName}.png`;
    }

    // Add method to get maximum value for specific appliances
    getMaxValueForAppliance(iconName: string): number {
        // Limit these three devices to maximum of 3
        const limitedAppliances = ['A.10', 'A.11', 'A.12']; // dishwasher, washing machine, dryer
        return limitedAppliances.includes(iconName) ? 3 : null;
    }

    /**
     * neue Settings speichern
     */
    saveSettings() {
        const errors: any = [];

        const Appliances = {};
        for (const el of this.appliances.items) {
            if (el.selected) {
                const maxLimit = this.getMaxValueForAppliance(el.iconName);
                Appliances[el.iconName] =
                    maxLimit !== null ? Math.min(el.count, maxLimit) : el.count;
            }
        }
        const Attributes = {
            Occupants: this.occupants.selectedValue,
            PropertyAge: this.propertyAge.selectedValue,
            PropertySize: this.propertySize.selectedValue,
            PropertyType: this.propertyType.selectedValue,
            SpaceHeating: this.spaceHeating.selectedValue,
            WaterHeating: this.waterHeating.selectedValue,
            Photovoltaic: this.photovoltaic.selectedValue,
        };

        const payload = { Appliances, Attributes };

        if (errors.length > 0) {
            for (const error of errors) {
                this.toastrService.warning(error);
            }
        } else {
            // Wenn Onboarding-Prozess - Google Analytics Event
            if (this.globals.getIsOnboarding()) {
                this.analytics.trackEvent({
                    action: 'onboarding_profile_end',
                    properties: {
                        category: 'Onboarding',
                    },
                });
                this.globals.resetIsOnboarding();
            }

            if (!this.application.isDemoMode()) {
                this.profile.setAttributes(payload).subscribe(
                    () => {
                        this.toastrService.success(
                            this.translate.instant('screens.household.settingsSaved')
                        );
                        this.initialize();
                    },
                    (error) => {
                        this.toastrService.error(
                            this.translate.instant('screens.household.errorOccurred'),
                            this.translate.instant('common.errors.error')
                        );
                        const error_obj = error.error;
                        if (error_obj.error.code === 180) {
                            console.log('Error:', error);
                        }
                    }
                );
            } else {
                this.toastrService.info(
                    this.translate.instant('screens.household.demoModeWarning')
                );
            }
        }
    }

    /**
     * Process contract data
     */
    private processContractData(data): void {
        if (this.application.isDemoMode()) {
            this.fullname = this.translate.instant('screens.household.demoUserName'); // Translate demo user name
        } else {
            this.fullname = data.profile.customer_name;
        }
    }

    /**
     * Process Profile information
     */
    private processProfileData(attributes): void {
        if ('Attributes' in attributes) {
            this.propertyType.selectedValue = attributes.Attributes.PropertyType;
            this.propertySize.selectedValue = attributes.Attributes.PropertySize;
            this.propertyAge.selectedValue = attributes.Attributes.PropertyAge;
            this.occupants.selectedValue = attributes.Attributes.Occupants;
            this.spaceHeating.selectedValue = attributes.Attributes.SpaceHeating;
            this.waterHeating.selectedValue = attributes.Attributes.WaterHeating;
            this.photovoltaic.selectedValue = attributes.Attributes.Photovoltaic;

            for (const element of this.appliances.items) {
                element.count = attributes.Appliances[element.iconName];
                element.selected = attributes.Appliances[element.iconName] > 0;
            }
        }
    }
}
